const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
]

// Matches "mon", "tue", "thur", "sat" etc. to a day name
const toDay = text => DAYS.find(day => day.toLowerCase().startsWith(text))

// Day ranges are typed by hand ("Mon-Fri", "Mon - Sun", "Sat", "Sun - Closed"),
// so pull out the day names and expand a range between two of them
const parseDays = dayRange => {
  const cleaned = (dayRange || "").toLowerCase().replace("closed", "")
  const tokens = (cleaned.match(/[a-z]+/g) || []).map(t => t.slice(0, 3))
  const days = tokens.map(toDay)

  if (!days.length || days.some(day => !day)) return null

  if (days.length === 2 && cleaned.includes("-")) {
    const start = DAYS.indexOf(days[0])
    const length = ((DAYS.indexOf(days[1]) - start + 7) % 7) + 1
    return Array.from({ length }, (_, i) => DAYS[(start + i) % 7])
  }

  return days
}

// Times are typed by hand too ("9.00am", "10:00am", "300pm", "4.00"). Returns
// 24 hour "HH:MM", or null if it can't be read. A time with no am/pm before
// 7 is taken as afternoon, since no showroom opens that early.
const parseTime = text => {
  const match = (text || "")
    .trim()
    .toLowerCase()
    .match(/^(\d{1,2})(?:[.:]?(\d{2}))?\s*(am|pm)?$/)

  if (!match) return null

  const [, rawHours, minutes = "00", meridiem] = match
  let hours = Number(rawHours)

  if (meridiem === "pm" && hours < 12) hours += 12
  if (meridiem === "am" && hours === 12) hours = 0
  if (!meridiem && hours < 7) hours += 12
  if (hours > 23 || Number(minutes) > 59) return null

  return `${String(hours).padStart(2, "0")}:${minutes}`
}

// Built from the hours shown on the page rather than the CMS microdata field,
// which has drifted out of step with them. Closed days and rows that can't be
// read are left out.
const buildOpeningHours = (openHours = []) =>
  (openHours || [])
    .map(({ dayRange, opens, closes }) => ({
      dayOfWeek: parseDays(dayRange),
      opens: parseTime(opens),
      closes: parseTime(closes)
    }))
    .filter(
      ({ dayOfWeek, opens, closes }) =>
        dayOfWeek && opens && closes && opens < closes
    )
    .map(hours => ({ "@type": "OpeningHoursSpecification", ...hours }))

const cleanAddressLine = line =>
  (line || "")
    .trim()
    .replace(/,$/, "")
    .trim()

export const buildShowroomSchema = ({ showroom, siteUrl }) => {
  const {
    businessName,
    path,
    addressLine1,
    addressLine2,
    city,
    state,
    postcode,
    hideAddress,
    primaryPhoneNumber,
    salesPhoneNumber,
    partsPhoneNumber,
    openHours,
    latitude,
    longitude,
    hero
  } = showroom

  const url = `${siteUrl.replace(/\/$/, "")}${path}`
  const openingHours = buildOpeningHours(openHours)
  const telephone = (
    primaryPhoneNumber ||
    salesPhoneNumber ||
    partsPhoneNumber ||
    ""
  ).trim()

  const schema = {
    "@context": "https://schema.org",
    "@type": "HotTubStore",
    "@id": `${url}#store`,
    name: businessName,
    url,
    image: hero?.url
  }

  if (telephone) schema.telephone = telephone

  if (!hideAddress) {
    schema.address = {
      "@type": "PostalAddress",
      streetAddress: [addressLine1, addressLine2]
        .map(cleanAddressLine)
        .filter(Boolean)
        .join(", "),
      addressLocality: city,
      // NZ showrooms have "NZ" as their state, which isn't a region
      ...(state && state !== "NZ" && { addressRegion: state }),
      postalCode: String(postcode),
      addressCountry: process.env.GATSBY_LANGUAGE.replace("en-", "")
    }
  }

  if (latitude != null && longitude != null) {
    schema.geo = { "@type": "GeoCoordinates", latitude, longitude }
  }

  if (openingHours.length) schema.openingHoursSpecification = openingHours

  return schema
}

export const buildBreadcrumbSchema = ({ breadcrumbs = [], siteUrl }) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: breadcrumbs.map(({ title, path }, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: title,
    item: `${siteUrl.replace(/\/$/, "")}${path}`
  }))
})
